import React, { useEffect, useRef } from "react";
import { useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";

import AssetImage from "src/components/wizard/Item/AssetImage";
import { ASSETS_COLLECTION_INVENTORY_MOVE_QUERY } from "src/constants/graphql/queries";

export default function GroupedAssetSearchResults({ searchCode, projectId, onAutoAdd, onSelect, onBack }) {
  const { loading, data } = useQuery(ASSETS_COLLECTION_INVENTORY_MOVE_QUERY, {
    variables: {
      search: searchCode,
      ...(projectId && { projectId }),
      hideDepletedAssets: true,
      order: { item_id: "desc" },
      type: "Asset",
    },
    skip: !searchCode,
  });
  const assets = data?.assetsCollection?.collection || [];
  const didAutoAddRef = useRef(false);

  useEffect(() => {
    if (!loading && assets.length === 1 && !didAutoAddRef.current) {
      didAutoAddRef.current = true;
      onAutoAdd(assets[0]);
    }
  }, [loading, assets]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (assets.length === 0) {
    return (
      <Box py={3} textAlign="center">
        <Typography color="textSecondary">No items found</Typography>
        <Button onClick={onBack} sx={{ mt: 1 }}>
          Search Again
        </Button>
      </Box>
    );
  }

  if (assets.length === 1) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={4}>
        <CircularProgress size={24} />
        <Typography ml={2}>Adding item...</Typography>
      </Box>
    );
  }

  return (
    <Box mt={2}>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Select an item to add:
      </Typography>
      {assets.map((asset, index) => {
        const depleted = asset.availableQuantity <= 0;
        return (
          <React.Fragment key={asset.id}>
            {index > 0 && <Divider />}
            <Box
              display="flex"
              p={1.5}
              gap={2}
              sx={{
                cursor: depleted ? "not-allowed" : "pointer",
                borderRadius: 1,
                "&:hover": depleted ? {} : { bgcolor: "rgba(0,0,0,0.04)" },
                ...(depleted && { opacity: 0.5 }),
              }}
              onClick={depleted ? undefined : () => onSelect(asset)}
            >
              <AssetImage width={70} height={60} image={asset?.images?.[0]?.url} />
              <Box flex={1}>
                <Typography variant="body2" fontWeight={500}>
                  {asset.itemId}
                  {asset.serialNumber ? ` | ${asset.serialNumber}` : ""}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {asset.description}
                </Typography>
                <Typography variant="body2">Available: {asset.availableQuantity}</Typography>
              </Box>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
}
